import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Box,
  Modal,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

const EditBrand = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [mainCategory, setMainCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [description, setDescription] = useState('');

  const navigate = useNavigate();
  const { brandId } = useParams();

  // Static data for pre-fill and dropdowns
  const staticBrandData = [
    { brandId: 1, mainCategory: 'Electronics', subCategory: 'Smartphones', brand: 'Samsung', description: 'Mobile phones and accessories' },
    { brandId: 2, mainCategory: 'Electronics', subCategory: 'Laptops', brand: 'Dell', description: 'Laptops and notebooks' },
    { brandId: 3, mainCategory: 'Clothing', subCategory: 'Men', brand: 'Nike', description: 'Clothing for men' },
    { brandId: 4, mainCategory: 'Clothing', subCategory: 'Women', brand: 'Adidas', description: 'Clothing for women' },
    { brandId: 5, mainCategory: 'Books', subCategory: 'Fiction', brand: 'Penguin', description: 'Fiction books' },
    { brandId: 6, mainCategory: 'Books', subCategory: 'Non-Fiction', brand: 'HarperCollins', description: 'Non-fiction books' },
  ];

  const staticMainCategoryData = [
    { mainCategoryId: 1, mainCategory: 'Electronics' },
    { mainCategoryId: 2, mainCategory: 'Clothing' },
    { mainCategoryId: 3, mainCategory: 'Books' },
    { mainCategoryId: 4, mainCategory: 'Home & Kitchen' },
    { mainCategoryId: 5, mainCategory: 'Sports' },
    { mainCategoryId: 6, mainCategory: 'Toys' },
  ];

  const staticSubCategoryData = {
    'Electronics': [
      { subCategoryId: 1, subCategory: 'Smartphones' },
      { subCategoryId: 2, subCategory: 'Laptops' },
    ],
    'Clothing': [
      { subCategoryId: 3, subCategory: 'Men' },
      { subCategoryId: 4, subCategory: 'Women' },
    ],
    'Books': [
      { subCategoryId: 5, subCategory: 'Fiction' },
      { subCategoryId: 6, subCategory: 'Non-Fiction' },
    ],
    'Home & Kitchen': [
      { subCategoryId: 7, subCategory: 'Appliances' },
      { subCategoryId: 8, subCategory: 'Furniture' },
    ],
    'Sports': [
      { subCategoryId: 9, subCategory: 'Outdoor' },
      { subCategoryId: 10, subCategory: 'Indoor' },
    ],
    'Toys': [
      { subCategoryId: 11, subCategory: 'Educational' },
      { subCategoryId: 12, subCategory: 'Action Figures' },
    ],
  };

  useEffect(() => {
    // Simulate fetching data for the brand to edit
    const brandToEdit = staticBrandData.find(data => data.brandId === parseInt(brandId));
    if (brandToEdit) {
      setMainCategory(brandToEdit.mainCategory);
      setSubCategory(brandToEdit.subCategory);
      setBrand(brandToEdit.brand);
      setDescription(brandToEdit.description);
    }
  }, [brandId]);

  const handleSave = (e) => {
    e.preventDefault();
    if (mainCategory && subCategory && brand && description) {
      setShowSuccessModal(true);
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: 'rgba(2, 78, 101, 1)',
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '0 auto',
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Edit Brand
        </Typography>
      </Box>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6, backgroundColor: '#f8fdfeff' }}>
        <div>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="main-category-label">Main Category</InputLabel>
            <Select
              labelId="main-category-label"
              id="main-category"
              value={mainCategory}
              label="Main Category"
              onChange={(e) => {
                setMainCategory(e.target.value);
                setSubCategory(''); // Reset subcategory when main category changes
              }}
            >
              {staticMainCategoryData.map((category) => (
                <MenuItem key={category.mainCategoryId} value={category.mainCategory}>
                  {category.mainCategory}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="sub-category-label">Sub Category</InputLabel>
            <Select
              labelId="sub-category-label"
              id="sub-category"
              value={subCategory}
              label="Sub Category"
              onChange={(e) => setSubCategory(e.target.value)}
              disabled={!mainCategory}
            >
              {mainCategory && staticSubCategoryData[mainCategory]?.map((subCat) => (
                <MenuItem key={subCat.subCategoryId} value={subCat.subCategory}>
                  {subCat.subCategory}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Brand"
            variant="outlined"
            fullWidth
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            sx={{ mb: 3 }}
          />
          <TextField
            label="Description"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              mr: 2,
              backgroundColor: '#127498ff',
              '&:hover': { backgroundColor: '#116280ff' }
            }}
          >
            UPDATE
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
          >
            CANCEL
          </Button>
        </div>
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Brand Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Brand Updated Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditBrand;
